import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { PageHeader, EmptyState } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Plus, CheckSquare, Trash2, Mail, List as ListIcon, Calendar as CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function priorityDot(p) {
  return { low: "bg-[#4A5568]", normal: "bg-[#C5A059]", high: "bg-red-700" }[p] || "bg-[#4A5568]";
}

function toISODate(d) {
  const yr = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const da = String(d.getDate()).padStart(2, "0");
  return `${yr}-${mo}-${da}`;
}

export default function TasksPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", due_date: "", priority: "normal" });
  const [filter, setFilter] = useState("all");
  const [view, setView] = useState("list"); // list | calendar
  const [cursor, setCursor] = useState(new Date()); // month view cursor

  const load = () => api.get("/tasks").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.title) return toast.error("Title required");
    await api.post("/tasks", form);
    setOpen(false);
    setForm({ title: "", description: "", due_date: "", priority: "normal" });
    load();
  };

  const toggle = async (t) => {
    await api.patch(`/tasks/${t.id}`, { status: t.status === "done" ? "open" : "done" });
    load();
  };
  const remove = async (id) => { await api.delete(`/tasks/${id}`); load(); };

  const remind = async (t) => {
    const { data } = await api.post(`/tasks/${t.id}/email-reminder`);
    if (data.status === "sent") {
      toast.success("Reminder email sent.");
    } else if (data.status === "disabled") {
      toast.message("Email is not configured", {
        description: "Add RESEND_API_KEY in the backend to enable reminders.",
      });
    } else {
      toast.error("Reminder failed: " + (data.detail || data.reason || "unknown"));
    }
  };

  const filtered = filter === "all" ? items : items.filter((t) => (filter === "open" ? t.status !== "done" : t.status === "done"));

  return (
    <div data-testid="tasks-page">
      <PageHeader
        eyebrow="Les Échéances"
        title="Deadlines & Tasks"
        description="Every reply period, every hearing, every filing — in one list or on a calendar."
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-none bg-[#2A0F5A] hover:bg-[#3D1F85] text-[#FAFAF8]" data-testid="tasks-new-btn">
                <Plus size={14} className="mr-1.5" /> New task
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none">
              <DialogHeader><DialogTitle className="font-serif text-2xl">New task</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <Input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-none border-[#E8E3F0]" data-testid="global-task-title-input" />
                <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="rounded-none border-[#E8E3F0]" />
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger className="rounded-none border-[#E8E3F0]"><SelectValue /></SelectTrigger>
                  <SelectContent>{["low", "normal", "high"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
                <Textarea rows={2} placeholder="Notes" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-none border-[#E8E3F0]" />
              </div>
              <DialogFooter>
                <Button className="rounded-none bg-[#2A0F5A] hover:bg-[#3D1F85] text-[#FAFAF8]" onClick={add} data-testid="global-task-save-btn">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex gap-2">
          {[["all", "All"], ["open", "Open"], ["done", "Done"]].map(([v, l]) => (
            <button
              key={v}
              onClick={() => setFilter(v)}
              className={`px-3 py-1.5 text-xs tracking-[0.18em] uppercase border ${filter === v ? "bg-[#2A0F5A] text-[#FAFAF8] border-[#2A0F5A]" : "bg-white border-[#E8E3F0] hover:border-[#2A0F5A]"}`}
              data-testid={`task-filter-${v}`}
            >
              {l}
            </button>
          ))}
        </div>
        <div className="flex border border-[#E8E3F0]">
          <button
            onClick={() => setView("list")}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs tracking-[0.18em] uppercase ${view === "list" ? "bg-[#2A0F5A] text-[#FAFAF8]" : "bg-white hover:bg-[#FAFAF8]"}`}
            data-testid="view-list-btn"
          >
            <ListIcon size={12} /> List
          </button>
          <button
            onClick={() => setView("calendar")}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs tracking-[0.18em] uppercase border-l border-[#E8E3F0] ${view === "calendar" ? "bg-[#2A0F5A] text-[#FAFAF8]" : "bg-white hover:bg-[#FAFAF8]"}`}
            data-testid="view-calendar-btn"
          >
            <CalendarIcon size={12} /> Calendar
          </button>
        </div>
      </div>

      {view === "list" ? (
        filtered.length === 0 ? (
          <EmptyState icon={CheckSquare} title="No tasks" description="Add personal deadlines or case-specific tasks." />
        ) : (
          <ul className="space-y-2">
            {filtered.map((t) => (
              <li key={t.id} className={`border border-[#E8E3F0] bg-white p-4 flex items-center gap-3 ${t.status === "done" ? "opacity-60" : ""}`} data-testid={`global-task-${t.id}`}>
                <input type="checkbox" checked={t.status === "done"} onChange={() => toggle(t)} className="w-4 h-4" />
                <span className={`w-2 h-2 rounded-full shrink-0 ${priorityDot(t.priority)}`} />
                <div className="flex-1 min-w-0">
                  <div className={`font-medium ${t.status === "done" ? "line-through" : ""}`}>{t.title}</div>
                  <div className="text-xs text-[#4A5568] mt-0.5">
                    {t.due_date ? `Due ${new Date(t.due_date).toLocaleDateString()}` : "No due date"} · {t.priority}
                  </div>
                </div>
                <button onClick={() => remind(t)} title="Email reminder" className="text-[#4A5568] hover:text-[#2A0F5A]" data-testid={`remind-task-${t.id}`}><Mail size={14} /></button>
                <button onClick={() => remove(t.id)} className="text-[#4A5568] hover:text-red-700" data-testid={`remove-task-${t.id}`}><Trash2 size={14} /></button>
              </li>
            ))}
          </ul>
        )
      ) : (
        <CalendarView cursor={cursor} setCursor={setCursor} items={filtered} onToggle={toggle} onRemind={remind} />
      )}
    </div>
  );
}

function CalendarView({ cursor, setCursor, items, onToggle, onRemind }) {
  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  // Mon=0 start
  const startOffset = (first.getDay() + 6) % 7;
  const daysInMonth = last.getDate();

  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);

  const tasksByDate = useMemo(() => {
    const map = {};
    for (const t of items) {
      if (!t.due_date) continue;
      const key = t.due_date.slice(0, 10);
      map[key] = map[key] || [];
      map[key].push(t);
    }
    return map;
  }, [items]);

  const today = toISODate(new Date());

  return (
    <div className="border border-[#E8E3F0] bg-white" data-testid="calendar-view">
      <div className="flex items-center justify-between px-5 py-3 border-b border-[#E8E3F0]">
        <div className="font-serif text-2xl">{MONTHS[month]} {year}</div>
        <div className="flex items-center gap-1">
          <button onClick={() => setCursor(new Date(year, month - 1, 1))} className="p-2 hover:bg-[#FAFAF8]" data-testid="cal-prev-btn"><ChevronLeft size={16} /></button>
          <button onClick={() => setCursor(new Date())} className="px-3 py-1 text-xs tracking-[0.18em] uppercase border border-[#E8E3F0] hover:border-[#2A0F5A]" data-testid="cal-today-btn">Today</button>
          <button onClick={() => setCursor(new Date(year, month + 1, 1))} className="p-2 hover:bg-[#FAFAF8]" data-testid="cal-next-btn"><ChevronRight size={16} /></button>
        </div>
      </div>
      <div className="grid grid-cols-7 border-b border-[#E8E3F0]">
        {DOW.map((d) => (
          <div key={d} className="px-3 py-2 label-eyebrow border-r border-[#E8E3F0] last:border-r-0">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {cells.map((d, i) => {
          const key = d ? toISODate(d) : `empty-${i}`;
          const dayTasks = d ? (tasksByDate[toISODate(d)] || []) : [];
          const isToday = d && toISODate(d) === today;
          return (
            <div
              key={key}
              className={`min-h-[110px] border-t border-r border-[#E8E3F0] last:border-r-0 p-2 ${d ? "bg-white" : "bg-[#FAFAF8]/50"} ${(i + 1) % 7 === 0 ? "border-r-0" : ""}`}
              data-testid={d ? `cal-cell-${toISODate(d)}` : undefined}
            >
              {d && (
                <>
                  <div className={`text-xs font-mono mb-1 ${isToday ? "inline-block bg-[#2A0F5A] text-[#FAFAF8] px-1.5 py-0.5" : "text-[#4A5568]"}`}>
                    {d.getDate()}
                  </div>
                  <div className="space-y-1">
                    {dayTasks.slice(0, 3).map((t) => (
                      <button
                        key={t.id}
                        onClick={() => onToggle(t)}
                        title={t.title}
                        className={`w-full text-left text-[11px] px-1.5 py-1 border truncate flex items-center gap-1 ${
                          t.status === "done"
                            ? "border-[#E8E3F0] text-[#4A5568] line-through"
                            : t.priority === "high"
                            ? "border-red-700/40 text-red-800 bg-red-700/5"
                            : "border-[#C5A059]/40 text-[#8a6b1f] bg-[#C5A059]/10"
                        }`}
                        data-testid={`cal-task-${t.id}`}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${priorityDot(t.priority)}`} />
                        {t.title}
                      </button>
                    ))}
                    {dayTasks.length > 3 && (
                      <div className="text-[10px] text-[#4A5568]">+{dayTasks.length - 3} more</div>
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
